import React, { useState, useEffect } from 'react';
import useAppStore from '../../store';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// KONSTANTEN & HELPER
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const ROLES: Record<string, string[]> = {
    'Produktiv (auch prod. Azubi)': ['Friseur', 'Friseur (Führung)', 'Kosmetischer MA', 'Prod. Azubi', 'Sonstiges'],
    'Unproduktiv': ['Reinigung', 'Rezeption', 'Social Media', 'Hausmeister', 'Office', 'Unprod. Anteil', 'Sonstiges'],
    'Azubi & Assistenz': ['Azubi', 'Assistenzkraft']
};

const getRoleTooltip = (type: string) => {
    if (type === 'Produktiv (auch prod. Azubi)') return 'Fachrichtung wählen (z.B. Friseur)';
    if (type === 'Unproduktiv') return 'Support-Rolle wählen (z.B. Rezeption)';
    if (type === 'Azubi & Assistenz') return 'Azubi oder Assistenz wählen';
    return 'Zuerst den Bereich wählen';
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }
        /* Spaltenbreiten für Neue MA */
        th.col-fixed-date, td.col-fixed-date { min-width: 170px !important; width: 170px !important; }
        th.col-fixed-name, td.col-fixed-name { min-width: 280px !important; }
        th.col-fixed-type, td.col-fixed-type { min-width: 220px !important; }
        th.col-fixed-role, td.col-fixed-role { min-width: 200px !important; }
        th.col-fixed-num,  td.col-fixed-num  { min-width: 140px !important; }
        
        .status-badge-grey { color: #94a3b8; font-size: 0.7rem; letter-spacing: 0.05em; font-weight: 700; }
        
        /* Input Error Rahmen für leere Pflichtfelder */
        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }

        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 1. AKTIVE ZEILE (Bearbeitbar)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    itemId: string; // ID des Drafts im newStaff Array
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ActiveNewStaffRow: React.FC<ActiveRowProps> = ({ itemId, onAdminLock }) => {
    const { clientData, settings, updateNewStaffDraft, removeNewStaffDraft, commitDraftState } = useAppStore();
    
    // Wir suchen das Item im Array
    const draft = clientData.drafts.newStaff.find(i => i.id === itemId);
    const currency = settings.country?.currency === 'CHF' ? 'CHF' : 'EUR';
    const productiveEmployees = (clientData.personalData || []).filter((p: any) => p && p.id);

    const fmt = (val: any, decimals: number = 2) => {
        if (val === '' || val === undefined) return '';
        return new Intl.NumberFormat('de-DE', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(val);
    };
    
    const parse = (val: string) => parseFloat(val.toString().replace(/\./g, '').replace(',', '.')) || 0;

    const [localValues, setLocalValues] = useState<any>({});

    // UNDO SYNCHRONISATION
    useEffect(() => {
        if (draft) {
            setLocalValues({
                ...draft.inputs,
                base: fmt(draft.inputs.base),
                add: fmt(draft.inputs.add),
                bonus: fmt(draft.inputs.bonus),
                hours: fmt(draft.inputs.hours, 1),
                days: fmt(draft.inputs.days, 0),
            });
        }
    }, [
        draft, 
        draft?.inputs?.base, draft?.inputs?.add, draft?.inputs?.bonus, 
        draft?.inputs?.hours, draft?.inputs?.days, draft?.inputs?.date, 
        draft?.inputs?.name, draft?.inputs?.type, draft?.inputs?.role, draft?.inputs?.notes
    ]);

    if (!draft) return null; 

    // HANDLER
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.select(); 
    };

    const handleBlur = (field: string) => {
        let val = localValues[field];
        // Erst Snapshot sichern
        commitDraftState();

        if (['base', 'add', 'bonus', 'hours', 'days'].includes(field)) {
            const num = parse(val);
            updateNewStaffDraft(itemId, field, num);
            const dec = field === 'hours' ? 1 : (field === 'days' ? 0 : 2);
            setLocalValues((prev: any) => ({ ...prev, [field]: fmt(num, dec) }));
        } else {
            updateNewStaffDraft(itemId, field, val);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            (e.target as HTMLElement).blur(); 
        }
    };

    const handleChange = (field: string, val: string) => {
        setLocalValues((prev: any) => ({ ...prev, [field]: val }));
    };

    const handleSelectChange = (field: string, val: string) => {
        commitDraftState();
        handleChange(field, val);
        updateNewStaffDraft(itemId, field, val);
    };

    // LOGIK
    const roleOptions = ROLES[localValues.type] || [];
    const calcs = draft.calculations;
    const roleTooltip = getRoleTooltip(localValues.type);
    const isDaysDisabled = localValues.type === 'Unproduktiv' || localValues.type === 'Azubi & Assistenz';
    const isLocked = draft.isLocked; // ADMIN STATUS

    const isUnprodAnteilRow = localValues.type === 'Unproduktiv' && localValues.role === 'Unprod. Anteil';
    const matchedProdByName = isUnprodAnteilRow
        ? productiveEmployees.find(
              (p: any) =>
                  String(p.id) === String(localValues.linkedProductiveEmployeeId || '') ||
                  (localValues.name && String(p.name).trim() === String(localValues.name).trim())
          )
        : null;
    const effectiveLinkedProdId = localValues.linkedProductiveEmployeeId || matchedProdByName?.id || '';
    const hasLinkedProd = isUnprodAnteilRow && Boolean(effectiveLinkedProdId);
    const isSplitActive = hasLinkedProd && localValues.isSplitDeduction !== false;

    const getProdReference = () => {
        if (!effectiveLinkedProdId) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        const prod = productiveEmployees.find((p: any) => String(p.id) === String(effectiveLinkedProdId));
        if (!prod) return { maxWage: Infinity, maxHours: Infinity, prodBaseWage: 0, prodBaseHours: 0 };
        const baseWage = Math.max(0, Number(prod.grundgehalt) || 0);
        const baseHours = Math.max(0, Number(prod.wochenstunden) || 0);
        return { maxWage: baseWage, maxHours: baseHours, prodBaseWage: baseWage, prodBaseHours: baseHours };
    };

    const handleToggleSplitCoupling = (nextSplit: boolean) => {
        if (!effectiveLinkedProdId || isLocked) return;
        commitDraftState();
        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: nextSplit,
        }));
        updateNewStaffDraft(itemId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateNewStaffDraft(itemId, 'isSplitDeduction', nextSplit);
    };

    const applySplitWageWithSyncedHours = (targetWage: number) => {
        if (isLocked) return;
        commitDraftState();
        if (!isSplitActive) {
            const freeWage = Math.max(0, Math.round(targetWage * 100) / 100);
            setLocalValues((prev: any) => ({ ...prev, base: fmt(freeWage, 2), isSplitDeduction: false }));
            updateNewStaffDraft(itemId, 'isSplitDeduction', false);
            updateNewStaffDraft(itemId, 'base', freeWage);
            return;
        }
        const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getProdReference();
        const clampedWage = Math.round(Math.min(Math.max(0, targetWage), maxWage) * 100) / 100;
        let nextHours = Math.max(0, parse(String(localValues.hours ?? 0)));
        if (prodBaseWage > 0 && prodBaseHours > 0) {
            const syncedHours = Math.round((prodBaseHours * (clampedWage / prodBaseWage)) * 10) / 10;
            nextHours = Math.min(Math.max(0, syncedHours), maxHours);
        }
        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: true,
            base: fmt(clampedWage, 2),
            hours: fmt(nextHours, 1),
        }));
        updateNewStaffDraft(itemId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateNewStaffDraft(itemId, 'isSplitDeduction', true);
        updateNewStaffDraft(itemId, 'base', clampedWage);
        updateNewStaffDraft(itemId, 'hours', nextHours);
    };

    const applySplitHoursWithSyncedWage = (targetHours: number) => {
        if (isLocked) return;
        commitDraftState();
        if (!isSplitActive) {
            const freeHours = Math.max(0, Math.round(targetHours * 10) / 10);
            setLocalValues((prev: any) => ({ ...prev, hours: fmt(freeHours, 1), isSplitDeduction: false }));
            updateNewStaffDraft(itemId, 'isSplitDeduction', false);
            updateNewStaffDraft(itemId, 'hours', freeHours);
            return;
        }
        const { maxWage, maxHours, prodBaseWage, prodBaseHours } = getProdReference();
        const clampedHours = Math.round(Math.min(Math.max(0, targetHours), maxHours) * 10) / 10;
        let nextWage = Math.max(0, parse(String(localValues.base ?? 0)));
        if (prodBaseHours > 0 && prodBaseWage > 0) {
            const syncedWage = Math.round((prodBaseWage * (clampedHours / prodBaseHours)) * 100) / 100;
            nextWage = Math.min(Math.max(0, syncedWage), maxWage);
        }
        setLocalValues((prev: any) => ({
            ...prev,
            linkedProductiveEmployeeId: effectiveLinkedProdId,
            isSplitDeduction: true,
            hours: fmt(clampedHours, 1),
            base: fmt(nextWage, 2),
        }));
        updateNewStaffDraft(itemId, 'linkedProductiveEmployeeId', effectiveLinkedProdId);
        updateNewStaffDraft(itemId, 'isSplitDeduction', true);
        updateNewStaffDraft(itemId, 'hours', clampedHours);
        updateNewStaffDraft(itemId, 'base', nextWage);
    };

    // Status Logik
    const renderStatus = () => {
        if (!draft.isComplete) return <span className="status-badge-grey">UNVOLLSTÄNDIG</span>;
        if (calcs.isMinWageIssue) return <span className="status-warn">PRÜFEN</span>;
        return <span className="status-ok">OK</span>;
    };

    const statusTooltip = !draft.isComplete
        ? "Bitte alle Pflichtfelder ausfüllen (Name, Bereich, Tätigkeit, Gehalt, Stunden)." 
        : (calcs.isMinWageIssue ? "Mindestlohn unterschritten!" : "Eingabe vollständig.");

    // Helper für fehlende Felder
    const isError = (val: any) => !val || val === '' || val === 0 || val === '0,00';
    const checkDays = !isDaysDisabled && isError(localValues.days);

    // CSS Klasse für gesperrte Zeilen
    const rowClass = `pe-row active-row ${isLocked ? 'locked-row' : ''}`;

    return (
        <tr className={rowClass}>
            {/* RESET (Löschen) */}
            <td className="col-tight" style={{ width: '1px' }}>
                <div className="pe-tooltip-wrapper" data-tooltip="Eintrag löschen">
                    <button className="reset-btn-row" onClick={() => removeNewStaffDraft(itemId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            {/* DATUM */}
            <td className="col-fixed-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Eintrittsdatum (Monatsanfang)">
                    <select 
                        className="table-select field-date input-active-look" 
                        value={localValues.date} 
                        onChange={(e) => handleSelectChange('date', e.target.value)}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* NAME */}
            <td className="col-fixed-name">
                {isUnprodAnteilRow ? (
                    <div className="pe-tooltip-wrapper" data-tooltip="Produktiven Mitarbeiter auswählen, dessen unproduktiver Anteil angelegt wird">
                        <select
                            className={`table-select ${isError(localValues.name) ? 'input-error' : 'active-value'}`}
                            value={effectiveLinkedProdId}
                            disabled={isLocked}
                            onChange={(e) => {
                                const selectedId = e.target.value;
                                const prodMa = productiveEmployees.find((p: any) => String(p.id) === String(selectedId));
                                commitDraftState();
                                const nextName = prodMa?.name ? String(prodMa.name) : '';
                                setLocalValues((prev: any) => ({
                                    ...prev,
                                    name: nextName,
                                    linkedProductiveEmployeeId: selectedId || undefined,
                                    isSplitDeduction: Boolean(selectedId),
                                }));
                                updateNewStaffDraft(itemId, 'name', nextName);
                                updateNewStaffDraft(itemId, 'linkedProductiveEmployeeId', selectedId || undefined);
                                updateNewStaffDraft(itemId, 'isSplitDeduction', Boolean(selectedId));
                            }}
                            style={{ textAlign: 'left', paddingLeft: '0.5rem' }}
                        >
                            <option value="">-- Prod. Mitarbeiter wählen --</option>
                            {productiveEmployees.map((p: any) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                        </select>
                    </div>
                ) : (
                    <div className="pe-tooltip-wrapper" data-tooltip="Name des neuen Mitarbeiters">
                        <div className="input-wrapper">
                            <input 
                                type="text" 
                                className={`table-input ${isError(localValues.name) ? 'input-error' : ''}`}
                                value={localValues.name} 
                                onChange={(e)=>handleChange('name', e.target.value)} 
                                onBlur={()=>handleBlur('name')}
                                onFocus={handleFocus}
                                onKeyDown={handleKeyDown}
                                style={{textAlign:'left', paddingLeft:'0.5rem'}}
                                placeholder="Name..."
                                disabled={isLocked}
                            />
                        </div>
                    </div>
                )}
            </td>

            {/* TYP */}
            <td className="col-fixed-type">
                <div className="pe-tooltip-wrapper" data-tooltip="Bereich wählen">
                    <select 
                        className={`table-select field-type ${isError(localValues.type) ? 'input-error' : ''}`}
                        value={localValues.type} 
                        onChange={(e) => handleSelectChange('type', e.target.value)}
                        disabled={isLocked}
                    >
                        <option value="">Bitte wählen</option>
                        <option value="Produktiv (auch prod. Azubi)">Produktiv</option>
                        <option value="Unproduktiv">Unproduktiv</option>
                        <option value="Azubi & Assistenz">Azubi/Assistenz</option>
                    </select>
                </div>
            </td>

            {/* TÄTIGKEIT */}
            <td className="col-fixed-role">
                <div className="pe-tooltip-wrapper" data-tooltip={roleTooltip}>
                    <select 
                        className={`table-select field-role ${isError(localValues.role) ? 'input-error' : ''}`}
                        value={localValues.role} 
                        onChange={(e) => handleSelectChange('role', e.target.value)}
                        disabled={isLocked}
                    >
                        <option value="">Bitte wählen</option>
                        {roleOptions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
            </td>

            {/* ZAHLENFELDER */}
            <td className="col-fixed-num" style={hasLinkedProd ? { minWidth: '220px' } : undefined}>
                <div className="pe-tooltip-wrapper" data-tooltip={hasLinkedProd ? `Unprod. Anteil (${isSplitActive ? 'Abzug aktiv' : 'Frei'})` : "Brutto-Grundgehalt"}>
                    <div className="flex items-center gap-1">
                        {hasLinkedProd && (
                            <div className="inline-flex shrink-0 items-center rounded border border-slate-700 overflow-hidden bg-slate-900">
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => handleToggleSplitCoupling(!isSplitActive)}
                                    title={isSplitActive ? 'Kopplung AKTIV (Abzug von Produktiv). Klicken für Frei.' : 'Kopplung AUS (Frei). Klicken für Abzug.'}
                                    className={`px-1.5 py-1 text-[9px] font-black uppercase leading-none border-r border-slate-700 transition-all ${
                                        isSplitActive ? 'bg-[#E6C299] text-black' : 'bg-slate-800 text-slate-400 hover:text-white'
                                    }`}
                                >
                                    {isSplitActive ? 'Abzug' : 'Frei'}
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(parse(String(localValues.base ?? 0)) - 100)}
                                    title="−100 € unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none text-slate-300 hover:bg-rose-500/20 hover:text-rose-400"
                                >
                                    −
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitWageWithSyncedHours(parse(String(localValues.base ?? 0)) + 100)}
                                    title="+100 € unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none border-l border-slate-700 text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400"
                                >
                                    +
                                </button>
                            </div>
                        )}
                        <div className="input-wrapper flex-1">
                            <input 
                                className={`table-input ${isError(localValues.base) ? 'input-error' : ''}`}
                                value={localValues.base} onChange={(e)=>handleChange('base', e.target.value)} onBlur={()=>handleBlur('base')} onFocus={handleFocus} onKeyDown={handleKeyDown} 
                                disabled={isLocked}
                            />
                            <div className="unit-label"><span className="unit-text">{currency}</span></div>
                        </div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Fixe Zulagen">
                    <div className="input-wrapper">
                        <input className="table-input" value={localValues.add} onChange={(e)=>handleChange('add', e.target.value)} onBlur={()=>handleBlur('add')} onFocus={handleFocus} onKeyDown={handleKeyDown} disabled={isLocked} />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Variable Boni (Ø)">
                    <div className="input-wrapper">
                        <input className="table-input" value={localValues.bonus} onChange={(e)=>handleChange('bonus', e.target.value)} onBlur={()=>handleBlur('bonus')} onFocus={handleFocus} onKeyDown={handleKeyDown} disabled={isLocked} />
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            
            {/* BRUTTO (Calc) */}
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Gesamt Brutto (Auto)">
                    <div className="result-wrapper">
                        <div className="table-calc">{fmt(calcs.bruttoTotal)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>

            <td className="col-fixed-num" style={hasLinkedProd ? { minWidth: '185px' } : undefined}>
                <div className="pe-tooltip-wrapper" data-tooltip="Wochenstunden">
                    <div className="flex items-center gap-1">
                        {hasLinkedProd && (
                            <div className="inline-flex shrink-0 items-center rounded border border-slate-700 overflow-hidden bg-slate-900">
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitHoursWithSyncedWage(parse(String(localValues.hours ?? 0)) - 1)}
                                    title="−1 Std. unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none text-slate-300 hover:bg-rose-500/20 hover:text-rose-400"
                                >
                                    −
                                </button>
                                <button
                                    type="button"
                                    disabled={isLocked}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => applySplitHoursWithSyncedWage(parse(String(localValues.hours ?? 0)) + 1)}
                                    title="+1 Std. unproduktiver Anteil"
                                    className="px-1.5 py-1 text-xs font-black leading-none border-l border-slate-700 text-[#E6C299] hover:bg-emerald-500/20 hover:text-emerald-400"
                                >
                                    +
                                </button>
                            </div>
                        )}
                        <div className="input-wrapper flex-1">
                            <input 
                                className={`table-input ${isError(localValues.hours) ? 'input-error' : ''}`}
                                value={localValues.hours} onChange={(e)=>handleChange('hours', e.target.value)} onBlur={()=>handleBlur('hours')} onFocus={handleFocus} onKeyDown={handleKeyDown} 
                                disabled={isLocked}
                            />
                            <div className="unit-label"><span className="unit-text">Std.</span></div>
                        </div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={isDaysDisabled ? "Nicht benötigt" : "Arbeitstage pro Woche"}>
                    <div className="input-wrapper">
                        <input 
                            className={`table-input ${checkDays ? 'input-error' : ''}`}
                            value={isDaysDisabled ? '0' : localValues.days} 
                            onChange={(e)=>handleChange('days', e.target.value)} 
                            onBlur={()=>handleBlur('days')} 
                            onFocus={handleFocus} 
                            onKeyDown={handleKeyDown}
                            disabled={isDaysDisabled || isLocked}
                            style={isDaysDisabled ? {color: '#475569', cursor: 'not-allowed'} : {}}
                        />
                        <div className="unit-label"><span className="unit-text">T.</span></div>
                    </div>
                </div>
            </td>

            {/* METRICS */}
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Stundenlohn (Basis)">
                    <div className="result-wrapper">
                        <div className="table-calc">{fmt(calcs.hourlyBase)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="Stundenlohn (Effektiv)">
                    <div className="result-wrapper">
                        <div className="table-calc">{fmt(calcs.hourlyTotal)}</div>
                        <div className="unit-label"><span className="unit-text">{currency}</span></div>
                    </div>
                </div>
            </td>
            
            {/* STATUS */}
            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip={statusTooltip}>
                    <div className={`status-container field-status ${calcs.isMinWageIssue ? 'warn-tooltip' : ''}`}>
                        {renderStatus()}
                    </div>
                </div>
            </td>

            <td className="col-fixed-num">
                <div className="pe-tooltip-wrapper" data-tooltip="SV-Satz (Auto)">
                    <div className="result-wrapper">
                        <div className="table-calc">{fmt(calcs.svRate, 1)}</div>
                        <div className="unit-label"><span className="unit-text">%</span></div>
                    </div>
                </div>
            </td>
            
            {/* NOTIZEN */}
            <td className="col-expand">
                <div className="pe-tooltip-wrapper" data-tooltip="Notizen...">
                    <textarea 
                        className="table-textarea" 
                        value={localValues.notes} 
                        onChange={(e)=>handleChange('notes', e.target.value)} 
                        onBlur={()=>handleBlur('notes')} 
                        placeholder="Notiz..."
                        disabled={isLocked}
                    ></textarea>
                </div>
            </td>
            
            {/* ADMIN SPERRE */}
            <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(itemId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(itemId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};


// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 2. JOKER ZEILE (Neuer Eintrag)
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerNewStaffRow: React.FC = () => {
    // FIX: Unused vars removed
    const { addNewStaffDraft, commitDraftState } = useAppStore();
    const [selectedDate, setSelectedDate] = useState('');

    const handleDateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        
        if (date) {
            commitDraftState();
            addNewStaffDraft();
            setTimeout(() => {
                const drafts = useAppStore.getState().clientData.drafts.newStaff;
                const lastItem = drafts[drafts.length - 1];
                if (lastItem) {
                    useAppStore.getState().updateNewStaffDraft(lastItem.id, 'date', date);
                }
            }, 0);
            setSelectedDate('');
        }
    };

    return (
        <tr className="pe-row">
            <td className="col-tight"><button className="reset-btn-row" disabled><i className="fas fa-undo text-stone-300 dark:text-slate-700"></i></button></td>

            {/* DATUM - DER TRIGGER */}
            <td className="col-fixed-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Datum wählen, um neuen Mitarbeiter anzulegen">
                    <select 
                        className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} 
                        value={selectedDate} 
                        onChange={handleDateSelect}
                    >
                        <option value="">Startdatum wählen</option>
                        {Array.from({length: 12}, (_, i) => {
                            const d = `01.${String(i+1).padStart(2,'0')}.`;
                            return <option key={d} value={d}>{d}</option>
                        })}
                    </select>
                </div>
            </td>

            {/* GHOST VALUES (Placeholder) */}
            <td className="col-fixed-name"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="Name..." /></div></td>
            <td className="col-fixed-type"><select className="table-select ghost-value" disabled><option>Bereich...</option></select></td> 
            <td className="col-fixed-role"><select className="table-select ghost-value" disabled><option>Tätigkeit...</option></select></td> 
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="0,00" /></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="0,00" /></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="0,00" /></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="0,0" /></div></td>
            <td className="col-fixed-num"><div className="input-wrapper"><input className="table-input ghost-value" disabled placeholder="0" /></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div></div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">0,00</div></div></td>
            <td className="col-fixed-num"><div className="status-container field-status status-neutral">--</div></td>
            <td className="col-fixed-num"><div className="result-wrapper"><div className="table-calc">—</div></div></td>
            <td className="col-expand"><textarea className="table-textarea" disabled placeholder="Notizen..."></textarea></td>
            <td><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};


// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// HAUPT-KOMPONENTE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface NewStaffTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const NewStaffTable: React.FC<NewStaffTableProps> = ({ onAdminLock }) => {
    const { clientData } = useAppStore();
    const newStaffDrafts = clientData.drafts.newStaff || [];

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2 min-w-[50px]">Reset</th>
                        <th className="col-fixed-date px-2">Gültig ab</th>
                        <th className="col-fixed-name px-2">Name (Neu)</th>
                        <th className="col-fixed-type px-2">Bereich/Typ</th>
                        <th className="col-fixed-role px-2">Tätigkeit</th>
                        <th className="col-fixed-num px-2">Grundgehalt</th>
                        <th className="col-fixed-num px-2">Lohnzusatz</th>
                        <th className="col-fixed-num px-2">Bonus</th>
                        <th className="col-fixed-num px-2">Bruttolohn</th>
                        <th className="col-fixed-num px-2">Std./Wo</th>
                        <th className="col-fixed-num px-2">Tage/Wo</th>
                        <th className="col-fixed-num px-2">Std. Lohn (G)</th>
                        <th className="col-fixed-num px-2">Std. Lohn (Ges)</th>
                        <th className="col-fixed-num px-2">Mindestlohn</th>
                        <th className="col-fixed-num px-2">SV-Satz</th>
                        <th className="col-expand px-2 min-w-[400px]">Info / Notizen</th>
                        <th className="col-tight px-2 min-w-[80px]">Admin</th>
                    </tr>
                </thead>
                <tbody id="newStaffBody">
                    {/* 1. AKTIVE DRAFTS */}
                    {newStaffDrafts.map(item => (
                        <ActiveNewStaffRow key={item.id} itemId={item.id} onAdminLock={onAdminLock} />
                    ))}

                    {/* 2. EINGABE-ZEILE (JOKER) */}
                    <JokerNewStaffRow />
                </tbody>
            </table>
        </div>
    );
};

export default NewStaffTable;